// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Shunt — bootstrap and the screen machine. Owns which screen is up, the room's Net (created ONCE
// per session and never left for a rematch), the yard's tap input, and the loop that ticks the
// clock and repaints. Rules live in rules.ts, yards in generate.ts, the race in race.ts.
//
// THE INPUT SCHEME, and why it is taps. Every move in this game has exactly ONE parameter. With
// the loco out on the headshunt the only choice is WHICH ROAD to propel into — the whole rake
// goes. With the loco standing in a road the only choice is WHERE TO UNCOUPLE — the destination
// is fixed, because the only way out is the way you came in. So a move is one decision, and one
// decision is one tap on the thing you mean: a road, or the wagon you want to cut behind. A drag
// would be a gesture carrying a parameter that does not exist, and it would put a hand over the
// road being worked. Targets are whole road bands (full width, >=52px tall) and whole wagons
// (>=44px), and only the legal ones are outlined, so an illegal tap is not expressible.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type Rounds, type RoundPlayer } from '@ben-gy/game-engine/rematch';
import { clearRoomInUrl, createLobby, createRoomEntry, normalizeRoomCode, setRoomInUrl } from '@ben-gy/game-engine/lobby';
import { resolveName } from '@ben-gy/game-engine/identity';

import { escapeHtml, fmtTime, moveLabel, parLabel } from './dom';
import { MODE_IDS, modeOf, type Mode } from './modes';
import {
  createGame,
  drainEvents,
  giveUp as giveUpGame,
  movesAvailable,
  play as playGame,
  resetYard,
  tick as tickGame,
  undo as undoGame,
  ROUND_LIMIT_MS,
  type GameEvent,
  type GameState,
} from './game';
import { sameMove, type Move } from './rules';
import { dailySeed, newSeed, seedToCode, todayUTC } from './seed';
import { YardView, type TapTarget } from './render';
import { Race, seatOf, createOneShot, type RaceMsg } from './race';
import { runCountdown } from './countdown';
import { aboutHtml, ATTRIB, helpHtml, menuHtml, multiResultsHtml, replayHtml, slipHtml, soloResultsHtml } from './ui';

const SLUG = 'shunt';

// ── shell ─────────────────────────────────────────────────────────────────────
const app = document.querySelector<HTMLElement>('#app')!;
let content: HTMLElement;

const store = createStore(SLUG);
const sfx = createSfx(store.get<boolean>('muted', false) ?? false);
const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

let unlocked = false;
function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  try {
    sfx.unlock();
  } catch {
    /* audio unlock is best-effort */
  }
}
window.addEventListener('pointerdown', unlock, { once: true });
window.addEventListener('keydown', unlock, { once: true });

// ── persistent session state ───────────────────────────────────────────────────
const playerName = resolveName(store, () => `Shunter ${Math.floor(Math.random() * 900 + 100)}`);
let myModeChoice = modeOf(store.get<string>('mode', '')).id;
let hostOptsMode = myModeChoice;

const bestKey = (id: string): string => `best_${id}`;
function bestFor(id: string): number | null {
  const v = store.get<number>(bestKey(id), -1) ?? -1;
  return v < 0 ? null : v;
}
/** Best = fewest moves on a solved yard. */
function recordBest(mode: Mode, moves: number): boolean {
  const cur = bestFor(mode.id);
  if (cur === null || moves < cur) {
    store.set(bestKey(mode.id), moves);
    return true;
  }
  return false;
}

let net: Net | null = null;
let rounds: Rounds | null = null;
let race: Race | null = null;
let roster: RoundPlayer[] = [];
let tally: number[] = [];
let roomCode = '';

// ── screen lifecycle ────────────────────────────────────────────────────────────
const cleanups: Array<() => void> = [];
function onCleanup(fn: () => void): void {
  cleanups.push(fn);
}
function teardown(): void {
  for (const fn of cleanups.splice(0)) {
    try {
      fn();
    } catch {
      /* a failing teardown must never block the next screen */
    }
  }
}
function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
}

function mountShell(): void {
  app.innerHTML = `<main class="main-content" id="content"></main><footer class="site-footer">${ATTRIB}</footer>`;
  content = document.getElementById('content')!;
}

/**
 * A modal sheet with all four ways out (the dismissal contract): a close button, a tap anywhere
 * outside, Escape, and a backdrop that is genuinely reachable. The backdrop dismiss arms late so
 * the opening tap's own trailing click cannot close the sheet it just opened.
 */
function sheet(html: string): void {
  const wrap = document.createElement('div');
  wrap.className = 'overlay';
  wrap.innerHTML = `<div class="sheet" role="dialog" aria-modal="true">${html}<button class="sheet-x sheet-close" aria-label="Close">✕</button></div>`;
  document.body.appendChild(wrap);
  let armed = false;
  const arm = setTimeout(() => {
    armed = true;
  }, 350);
  const close = (): void => {
    clearTimeout(arm);
    window.removeEventListener('keydown', onKey);
    wrap.remove();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  window.addEventListener('keydown', onKey);
  wrap.addEventListener('pointerdown', (e) => {
    if (armed && e.target === wrap) close();
  });
  wrap.querySelectorAll('.sheet-close').forEach((b) => b.addEventListener('click', close));
}

function toast(msg: string): void {
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.classList.add('show'), 10);
  setTimeout(() => t.remove(), 1900);
}

// ── menu ────────────────────────────────────────────────────────────────────────
function showMenu(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = menuHtml({ modeId: myModeChoice, best: bestFor(myModeChoice), muted: sfx.muted(), dateStr: todayUTC() });

  content.querySelectorAll<HTMLElement>('.chip').forEach((c) =>
    c.addEventListener('click', () => {
      myModeChoice = modeOf(c.dataset.mode).id;
      store.set('mode', myModeChoice);
      try {
        sfx.play('select');
      } catch {
        /* best-effort */
      }
      showMenu();
    }),
  );
  content.querySelector('#playDaily')!.addEventListener('click', () => startSolo(true));
  content.querySelector('#playRandom')!.addEventListener('click', () => startSolo(false));
  content.querySelector('#playFriends')!.addEventListener('click', () => beginFriends());
  content.querySelector('#help')!.addEventListener('click', () => sheet(helpHtml()));
  content.querySelector('#about')!.addEventListener('click', () => sheet(aboutHtml()));
  content.querySelector('#mute')!.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });

  if (!store.get<boolean>('seenHelp', false)) {
    store.set('seenHelp', true);
    sheet(helpHtml());
  }
}

// ── juice ──────────────────────────────────────────────────────────────────────
function juice(events: GameEvent[]): void {
  for (const e of events) {
    try {
      if (e.t === 'push') sfx.play('hit', { pitch: 0.55 + Math.min(3, e.n ?? 0) * 0.05 });
      else if (e.t === 'pull') sfx.play('blip', { pitch: 0.7 + Math.min(3, e.n ?? 0) * 0.12 });
      else if (e.t === 'round') sfx.play('powerup', { pitch: 0.8 });
      else if (e.t === 'seat') sfx.play('coin', { pitch: 0.9 + Math.min(5, e.n ?? 1) * 0.14 });
      else if (e.t === 'unseat') sfx.play('blip', { pitch: 0.4 });
      else if (e.t === 'solve') sfx.play('win');
      else if (e.t === 'undo') sfx.play('select', { pitch: 0.6 });
      else if (e.t === 'reject') sfx.play('lose', { pitch: 1.6 });
    } catch {
      /* audio best-effort */
    }
    if (!reducedMotion && 'vibrate' in navigator) {
      try {
        if (e.t === 'seat') navigator.vibrate(12);
        else if (e.t === 'solve') navigator.vibrate([12, 30, 12]);
      } catch {
        /* haptics best-effort */
      }
    }
  }
}

// ── the game screen (shared by solo + multiplayer) ────────────────────────────────
interface GameConfig {
  mode: Mode;
  state: () => GameState;
  play: (m: Move) => GameEvent[];
  undo: () => GameEvent[];
  reset: () => GameEvent[];
  advance: (dtMs: number) => GameEvent[];
  isOver: () => boolean;
  onOver: () => void;
  onQuit: () => void;
  onGiveUp: () => void;
  allowRestart: boolean;
  onRestart?: () => void;
  rivals?: () => Array<{ name: string; moves: number; seated: number; done: boolean; won: boolean }>;
}
interface GameScreen {
  destroy(): void;
}

// Exposed only so the browser-verification hook can drive the REAL play path synchronously (rAF is
// throttled in a hidden tab). Never read by gameplay.
let activePlay: ((m: Move) => void) | null = null;
let activeView: YardView | null = null;
let activeLive = false;
/**
 * Repaint the live game screen. A rival's status arriving is a reason to repaint the HUD, not just
 * the results screen — without this the rival strip only ever updated when the LOCAL player moved,
 * so in a race you watched a frozen counter until you happened to take a turn yourself. Found by
 * the two-peer smoke test; the wire was fine, the repaint was missing.
 */
let activeRefresh: (() => void) | null = null;

function buildGameScreen(cfg: GameConfig): GameScreen {
  const mode = cfg.mode;
  const st0 = cfg.state();
  content.innerHTML = `
    <section class="screen game">
      <div class="hud">
        <span class="hud-mode">${escapeHtml(mode.name)}</span>
        <span class="hud-moves" id="hudMoves"></span>
        <span class="hud-timer" id="hudTimer"></span>
        <button class="iconbtn" id="pause" title="Pause" aria-label="Pause">❚❚</button>
      </div>
      <div id="slipHost">${slipHtml(st0.yard.target, st0.seated)}</div>
      <div class="rivals" id="rivals" hidden></div>
      <div class="yard-wrap"><div class="yard" id="yardHost"></div></div>
      <p class="hint" id="hint"></p>
      <div class="controls">
        <button class="btn small" id="undo">Set back</button>
        <button class="btn small" id="reset">Reset yard</button>
        <button class="btn small" id="around" hidden>Run round</button>
        <button class="btn small ghost" id="quit">Quit</button>
      </div>
    </section>`;

  const yardHost = content.querySelector<HTMLElement>('#yardHost')!;
  const slipHost = content.querySelector<HTMLElement>('#slipHost')!;
  const hudMoves = content.querySelector<HTMLElement>('#hudMoves')!;
  const hudTimer = content.querySelector<HTMLElement>('#hudTimer')!;
  const hintEl = content.querySelector<HTMLElement>('#hint')!;
  const rivalsEl = content.querySelector<HTMLElement>('#rivals')!;
  const aroundBtn = content.querySelector<HTMLButtonElement>('#around')!;

  const view = new YardView(yardHost);
  activeView = view;
  view.mount(st0.yard);

  // A backgrounded tab measures 0 and would produce NaN geometry, so retry until a real box
  // exists — rAF never fires in a hidden tab, which is why the backstop is a timeout.
  const doResize = (): void => {
    if (!view.resize()) setTimeout(doResize, 120);
  };
  requestAnimationFrame(doResize);
  setTimeout(doResize, 60);
  window.addEventListener('resize', doResize);
  window.addEventListener('orientationchange', doResize);
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => view.resize()) : null;
  ro?.observe(yardHost);

  let live = false;
  let paused = false;
  let overHandled = false;
  let destroyed = false;
  let last = performance.now();
  let raf = 0;

  /** Turn the legal move list into the taps that express it. */
  function targetsFor(g: GameState): { targets: TapTarget[]; moves: Array<{ target: TapTarget; move: Move }> } {
    const out: Array<{ target: TapTarget; move: Move }> = [];
    const at = g.yard.locoAt;
    for (const m of movesAvailable(g)) {
      if (m.t === 'push') out.push({ target: { kind: 'road', track: m.to }, move: m });
      else if (m.t === 'pull') {
        if (m.k === 0) {
          out.push({ target: { kind: 'loco', track: at }, move: m });
          const parent = g.yard.spec.tracks[at].parent;
          if (parent !== null) out.push({ target: { kind: 'road', track: parent }, move: m });
        } else {
          out.push({ target: { kind: 'car', track: at, index: m.k - 1 }, move: m });
        }
      }
    }
    return { targets: out.map((o) => o.target), moves: out };
  }

  function resolveTap(g: GameState, hit: TapTarget): Move | null {
    const { moves } = targetsFor(g);
    // a wagon on a road the loco is NOT standing on reads as "go to that road"
    const norm: TapTarget =
      hit.kind === 'car' && hit.track !== g.yard.locoAt ? { kind: 'road', track: hit.track } : hit;
    let best: Move | null = null;
    for (const o of moves) {
      if (o.target.kind !== norm.kind) continue;
      if (o.target.track !== norm.track) continue;
      if (norm.kind === 'car' && o.target.index !== norm.index) continue;
      best = o.move;
      break;
    }
    return best;
  }

  const doPlay = (m: Move): void => {
    if (destroyed || !live || paused || cfg.isOver()) return;
    const ev = cfg.play(m);
    afterChange(ev);
  };
  activePlay = doPlay;
  activeRefresh = () => {
    if (!destroyed) refresh();
  };

  function afterChange(ev: GameEvent[]): void {
    const g = cfg.state();
    juice(ev);
    if (ev.some((e) => e.t === 'seat')) view.celebrate(g.yard.spec.departure, reducedMotion);
    if (ev.some((e) => e.t === 'reject')) view.reject(reducedMotion);
    refresh();
  }

  function onTap(e: PointerEvent): void {
    if (destroyed || !live || paused || cfg.isOver()) return;
    const g = cfg.state();
    const hit = view.hit(e.target);
    if (!hit) return;
    const m = resolveTap(g, hit);
    if (!m) {
      view.reject(reducedMotion);
      try {
        sfx.play('lose', { pitch: 1.7 });
      } catch {
        /* best-effort */
      }
      return;
    }
    doPlay(m);
  }
  yardHost.addEventListener('pointerdown', onTap);

  function refresh(): void {
    const g = cfg.state();
    const { targets } = targetsFor(g);
    view.setTargets(live && !paused && !g.over ? targets : []);
    slipHost.innerHTML = slipHtml(g.yard.target, g.seated);

    const over = g.moves - g.par;
    hudMoves.innerHTML = `<b>${g.moves}</b> <span class="muted">/ par ${g.par}</span>`;
    hudMoves.classList.toggle('over', over > 0);
    hudTimer.textContent = fmtTime(Math.max(0, ROUND_LIMIT_MS - g.elapsedMs));

    const atRoot = g.yard.spec.tracks[g.yard.locoAt].parent === null;
    hintEl.textContent = g.over
      ? ''
      : atRoot
        ? g.yard.cars[g.yard.locoAt].length
          ? 'Tap a road to propel the rake in.'
          : 'Tap a road to run the loco in.'
        : 'Tap a wagon to draw it out — or the loco to come out light.';

    const canRound = movesAvailable(g).some((m) => m.t === 'round');
    aroundBtn.hidden = !g.mode.spec.loop;
    aroundBtn.disabled = !canRound;

    if (cfg.rivals) {
      const rs = cfg.rivals();
      if (rs.length) {
        rivalsEl.hidden = false;
        rivalsEl.innerHTML = rs
          .map((r) => {
            const prog = r.done ? (r.won ? `${r.moves}✓` : '—') : `${r.seated} up · ${r.moves}`;
            return `<span class="rival${r.done ? ' done' : ''}">${escapeHtml(r.name)} <b>${prog}</b></span>`;
          })
          .join('');
      }
    }
  }

  content.querySelector('#undo')!.addEventListener('click', () => {
    if (!live || paused || cfg.isOver()) return;
    afterChange(cfg.undo());
  });
  content.querySelector('#reset')!.addEventListener('click', () => {
    if (!live || paused || cfg.isOver()) return;
    afterChange(cfg.reset());
  });
  aroundBtn.addEventListener('click', () => doPlay({ t: 'round' }));
  content.querySelector('#quit')!.addEventListener('click', cfg.onQuit);

  const cd = runCountdown(yardHost, sfx, () => {
    if (destroyed) return;
    live = true;
    activeLive = true;
    last = performance.now();
    refresh();
  });

  function loop(now: number): void {
    if (destroyed) return;
    const dt = Math.min(250, now - last);
    last = now;
    if (live && !paused && !cfg.isOver()) {
      const ev = cfg.advance(dt);
      if (ev.length) juice(ev);
    }
    const g = cfg.state();
    hudTimer.textContent = fmtTime(Math.max(0, ROUND_LIMIT_MS - g.elapsedMs));
    if (cfg.isOver() && !overHandled) {
      overHandled = true;
      refresh();
      setTimeout(() => {
        if (!destroyed) cfg.onOver();
      }, 780);
    }
    raf = requestAnimationFrame(loop);
  }
  raf = requestAnimationFrame(loop);
  refresh();

  function setPaused(p: boolean): void {
    if (destroyed || cfg.isOver()) return;
    paused = p;
    let ov = yardHost.querySelector<HTMLElement>('.pause-ov');
    if (p) {
      if (!ov) {
        ov = document.createElement('div');
        ov.className = 'pause-ov';
        ov.innerHTML = `<div class="pause-card"><h2>Paused</h2>
          <button class="btn primary" id="resume">Resume</button>
          ${cfg.allowRestart ? '<button class="btn" id="restart">New yard</button>' : ''}
          <button class="btn" id="phelp">How to play</button>
          <button class="btn ghost" id="pgive">Give up</button></div>`;
        yardHost.appendChild(ov);
        ov.querySelector('#resume')!.addEventListener('click', () => setPaused(false));
        ov.querySelector('#restart')?.addEventListener('click', () => cfg.onRestart?.());
        ov.querySelector('#phelp')!.addEventListener('click', () => sheet(helpHtml()));
        ov.querySelector('#pgive')!.addEventListener('click', () => {
          setPaused(false);
          cfg.onGiveUp();
        });
      }
    } else {
      ov?.remove();
      last = performance.now();
    }
    refresh();
  }
  content.querySelector('#pause')!.addEventListener('click', () => setPaused(!paused));

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'p' || e.key === 'P') setPaused(!paused);
    else if (e.key === 'u' || e.key === 'U') {
      if (live && !paused && !cfg.isOver()) afterChange(cfg.undo());
    } else if ((e.key === 'r' || e.key === 'R') && cfg.allowRestart) cfg.onRestart?.();
    else if (/^[1-9]$/.test(e.key)) {
      // number keys pick the road at that row, or the cut depth when standing in one
      const g = cfg.state();
      const n = Number(e.key) - 1;
      const atRoot = g.yard.spec.tracks[g.yard.locoAt].parent === null;
      const hit: TapTarget = atRoot ? { kind: 'road', track: n } : { kind: 'car', track: g.yard.locoAt, index: n };
      const m = resolveTap(g, hit);
      if (m) doPlay(m);
    } else if (e.key === '0' || e.key === 'Escape') {
      const g = cfg.state();
      const m = resolveTap(g, { kind: 'loco', track: g.yard.locoAt });
      if (m) doPlay(m);
    }
  }
  window.addEventListener('keydown', onKey);

  return {
    destroy() {
      destroyed = true;
      activeLive = false;
      activeRefresh = null;
      if (activePlay === doPlay) activePlay = null;
      if (activeView === view) activeView = null;
      cancelAnimationFrame(raf);
      cd.cancel();
      yardHost.removeEventListener('pointerdown', onTap);
      window.removeEventListener('resize', doResize);
      window.removeEventListener('orientationchange', doResize);
      window.removeEventListener('keydown', onKey);
      ro?.disconnect();
      view.destroy();
    },
  };
}

// ── solo ──────────────────────────────────────────────────────────────────────────
let soloState: GameState | null = null;
let soloDaily = false;
let soloScreen: GameScreen | null = null;

function makeSoloConfig(mode: Mode, onRestart: () => void): GameConfig {
  return {
    mode,
    state: () => soloState!,
    play: (m) => {
      playGame(soloState!, m);
      return drainEvents(soloState!);
    },
    undo: () => {
      undoGame(soloState!);
      return drainEvents(soloState!);
    },
    reset: () => {
      resetYard(soloState!);
      return drainEvents(soloState!);
    },
    advance: (dt) => {
      tickGame(soloState!, dt);
      return drainEvents(soloState!);
    },
    isOver: () => soloState!.over,
    onOver: () => endSolo(),
    onQuit: () => showMenu(),
    onGiveUp: () => {
      giveUpGame(soloState!);
      drainEvents(soloState!);
    },
    allowRestart: true,
    onRestart,
  };
}

function startSolo(daily: boolean): void {
  teardown();
  setPlaying(true);
  const mode = modeOf(myModeChoice);
  soloDaily = daily;
  const seed = daily ? dailySeed(mode.id, todayUTC()) : newSeed();
  soloState = createGame(mode, seed);
  soloScreen = buildGameScreen(makeSoloConfig(mode, () => startSolo(soloDaily)));
  onCleanup(() => soloScreen?.destroy());
}

function startSeed(seed: number): void {
  teardown();
  setPlaying(true);
  const mode = modeOf(myModeChoice);
  soloDaily = false;
  soloState = createGame(mode, seed);
  soloScreen = buildGameScreen(makeSoloConfig(mode, () => startSolo(false)));
  onCleanup(() => soloScreen?.destroy());
}

function endSolo(): void {
  const st = soloState!;
  const isNewBest = st.finished ? recordBest(st.mode, st.moves) : false;
  showSoloResults(isNewBest);
}

function showSoloResults(isNewBest: boolean): void {
  teardown();
  setPlaying(false);
  const st = soloState!;
  const mode = st.mode;
  content.innerHTML = `
    ${soloResultsHtml({
      mode,
      won: st.finished,
      moves: st.moves,
      par: st.par,
      undos: st.undos,
      ms: st.elapsedMs,
      best: bestFor(mode.id),
      daily: soloDaily,
      isNewBest,
    })}
    <div class="menu-actions">
      <button class="btn primary" id="again">New yard</button>
      <button class="btn" id="replay">Watch the best line</button>
      <button class="btn" id="share">Share</button>
      <button class="btn ghost" id="menu">Main menu</button>
      <button class="btn ghost results-feedback" id="feedback">Report a problem</button>
    </div>
    <div id="replayHost"></div>`;
  content.querySelector('#again')!.addEventListener('click', () => startSolo(soloDaily));
  content.querySelector('#menu')!.addEventListener('click', showMenu);
  content.querySelector('#replay')!.addEventListener('click', () => showReplay(st));
  content.querySelector('#feedback')!.addEventListener('click', (e) =>
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
  content.querySelector('#share')!.addEventListener('click', () => void shareSolo(st, mode));
}

/**
 * Play the proven-shortest line back, move by move (principle #9: show what everyone missed). It
 * is the same line the solver produced when the yard was made, so it is not a hint engine — it is
 * the answer, offered only once the round is over.
 */
function showReplay(st: GameState): void {
  const host = content.querySelector<HTMLElement>('#replayHost');
  if (!host) return;
  host.innerHTML = `<div class="replay">${replayHtml(st.par)}<div class="replay-yard" id="replayYard"></div></div>`;
  const yardHost = host.querySelector<HTMLElement>('#replayYard')!;
  const demo = createGame(st.mode, st.seed);
  const view = new YardView(yardHost);
  view.mount(demo.yard);
  requestAnimationFrame(() => view.resize());
  setTimeout(() => view.resize(), 60);
  let i = 0;
  const line = demo.puzzle.line;
  const timer = setInterval(() => {
    if (i >= line.length) {
      clearInterval(timer);
      return;
    }
    playGame(demo, line[i++]);
    drainEvents(demo);
    view.paint();
  }, 720);
  onCleanup(() => {
    clearInterval(timer);
    view.destroy();
  });
  host.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest' });
}

function shareSolo(st: GameState, mode: Mode): Promise<void> {
  const head = soloDaily ? `Shunt ${todayUTC()} · ${mode.name}` : `Shunt · ${mode.name}`;
  const line = st.finished
    ? `made the train up in ${moveLabel(st.moves)} (par ${st.par}, ${parLabel(st.moves, st.par)})`
    : `left this one in the yard — par was ${st.par}`;
  const link = soloDaily
    ? 'https://shunt.benrichardson.dev'
    : `https://shunt.benrichardson.dev/?seed=${seedToCode(st.seed)}&m=${mode.id}`;
  return share(`${head} — ${line}\n${link}`);
}

async function share(text: string): Promise<void> {
  try {
    if (navigator.share) {
      await navigator.share({ text });
      return;
    }
  } catch {
    /* cancelled — fall through to copy */
  }
  try {
    await navigator.clipboard.writeText(text);
    toast('Copied to clipboard');
  } catch {
    toast('Could not copy');
  }
}

// ── multiplayer ───────────────────────────────────────────────────────────────────
let netSend: (m: RaceMsg) => void = () => {};
let stOff: () => void = () => {};
let lobby: { repaint(): void; destroy(): void } | null = null;
let lobbyMounted = false;
let gameScreen: GameScreen | null = null;

function repaintLobby(): void {
  if (lobbyMounted) lobby?.repaint();
}

function beginFriends(): void {
  showRoomEntry();
}

/**
 * A room is a choice, not a destiny (principle #11). An invite link is honoured ONCE per page
 * load: `enterRoom` puts the code back in the URL so the tab can be shared or reloaded while you
 * are in the room, which means the raw URL is no longer evidence that you WANT to be in it. Without
 * this latch, leaving a room and tapping "Play with friends" walked straight back into the room you
 * just left, with no way to start a new one.
 */
let deepLinkUsed = false;

function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = '<section class="screen"><div id="entry"></div></section>';
  const host = content.querySelector<HTMLElement>('#entry')!;

  const deep = deepLinkUsed ? null : new URL(location.href).searchParams.get('room');
  if (deep) {
    deepLinkUsed = true;
    const code = normalizeRoomCode(deep);
    clearRoomInUrl();
    if (code.length >= 3) {
      void enterRoom(code, false);
      return;
    }
  }

  const entry = createRoomEntry({
    container: host,
    onSubmit: (code: string, created: boolean) => void enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with friends',
    subtitle: 'Everyone shunts the same yard. Fewest moves wins. Start a room, or type a friend’s code.',
  });
  onCleanup(() => entry.destroy());
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  tally = [];
  race = null;
  soloState = null; // a live room supersedes any lingering solo yard (keeps the test hook honest)

  content.innerHTML = `<section class="screen"><div class="lobby-searching"><span class="spinner"></span> Connecting…</div></section>`;

  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: (_id, isSelfHost) => {
        race?.setHost(isSelfHost);
        repaintLobby();
      },
      onPeers: () => repaintLobby(),
      onPeerLeave: (id) => {
        const seat = seatOf(roster, id);
        if (seat !== null) race?.peerLeft(seat);
        repaintLobby();
        refreshMultiResults();
      },
    },
  );
  net = n;

  const r = createRounds<{ mode: string }>({
    net: n,
    playerName,
    minPlayers: 2,
    roundOpts: () => ({ mode: myModeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (s) => {
      const ho = s.hostOpts as { mode?: string } | null;
      if (ho && typeof ho.mode === 'string') hostOptsMode = modeOf(ho.mode).id;
      repaintLobby();
    },
  });
  rounds = r;

  const sendStatus = n.channel<RaceMsg>('st', (data, from) => {
    if (!race) return;
    const seat = seatOf(roster, from);
    if (seat === null) return;
    race.receive(data, seat);
    refreshMultiResults();
  });
  netSend = (m) => sendStatus(m);
  stOff = () => sendStatus.off();

  showLobby();
}

function showLobby(): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return;
  lobbyMounted = true;
  setPlaying(false);

  content.innerHTML = `<section class="screen"><div id="lobby-host"></div></section>`;
  const isHost = (): boolean => n.isHost() && n.hostSettled();

  lobby = createLobby({
    container: content.querySelector<HTMLElement>('#lobby-host')!,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: 2,
    maxPlayers: 6,
    onCancel: () => void leaveRoom(),
    modeSlot: () => {
      if (isHost()) {
        return `<div class="lobby-modes" id="lm">${MODE_IDS.map(
          (id) =>
            `<button class="chip mode-btn${id === myModeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === myModeChoice}">${escapeHtml(
              modeOf(id).name,
            )}</button>`,
        ).join('')}<p class="mode-blurb">${escapeHtml(modeOf(myModeChoice).blurb)}</p></div>`;
      }
      return `<p class="host-pick">${
        n.hostSettled() ? `The host chose <strong>${escapeHtml(modeOf(hostOptsMode).name)}</strong>` : 'Waiting for the host…'
      }</p>`;
    },
    onModeMount: () => {
      if (!isHost()) return;
      content.querySelectorAll<HTMLElement>('#lm .mode-btn').forEach((b) =>
        b.addEventListener('click', () => {
          myModeChoice = modeOf(b.dataset.mode).id;
          store.set('mode', myModeChoice);
          lobby?.repaint();
        }),
      );
    },
  });
  onCleanup(() => {
    lobby?.destroy();
    lobby = null;
  });
}

function startNetRound(info: RoundInfo<{ mode: string }>): void {
  teardown();
  lobbyMounted = false;
  roster = info.players;
  if (tally.length !== roster.length) tally = Array(roster.length).fill(0);
  const mode = modeOf(info.opts?.mode);
  const selfSeat = info.seated ? seatOf(roster, net?.selfId ?? '') : null;
  const names = roster.map((p) => p.name);

  const rc = new Race({
    mode,
    seed: info.seed,
    selfSeat,
    names,
    isHost: info.isHost,
    deps: {
      send: (m) => netSend(m),
      onChange: () => {
        activeRefresh?.(); // a rival moved — repaint the live HUD, not only the results screen
        refreshMultiResults();
      },
      onSelfEnd: () => {
        rounds?.finish();
        setTimeout(() => showMultiResults(), 800);
      },
    },
  });
  race = rc;
  setPlaying(true);

  if (selfSeat === null) {
    showMultiResults();
    return;
  }

  gameScreen = buildGameScreen({
    mode,
    state: () => rc.state,
    play: (m) => rc.play(m),
    undo: () => rc.undo((g) => undoGame(g)),
    reset: () => rc.undo((g) => (resetYard(g), true)),
    advance: (dt) => rc.tick(dt),
    isOver: () => rc.state.over,
    onOver: () => {
      /* onSelfEnd already scheduled the results */
    },
    onQuit: () => backToLobby(),
    onGiveUp: () => rc.giveUp(),
    allowRestart: false,
    rivals: () =>
      [...rc.results.values()]
        .filter((p) => p.seat !== rc.selfSeat && !p.left)
        .map((p) => ({ name: p.name, moves: p.moves, seated: p.seated, done: p.done, won: p.won })),
  });
  onCleanup(() => {
    gameScreen?.destroy();
    gameScreen = null;
  });
}

// ── multiplayer results ──────────────────────────────────────────────────────────
let resultsMounted = false;
let winsAwarded = false;
const closeWait = createOneShot(25_000, () => refreshMultiResults());

function showMultiResults(): void {
  teardown();
  setPlaying(false);
  resultsMounted = true;
  winsAwarded = false;
  content.innerHTML = `
    <section class="screen results" id="mres"></section>
    <div class="menu-actions" id="mact">
      <button class="btn primary" id="again">Play again</button>
      <p class="waiting-note" id="waiting" aria-live="polite"></p>
      <button class="btn" id="replay">Watch the best line</button>
      <button class="btn" id="lobby">Back to lobby</button>
      <button class="btn ghost" id="menu">Main menu</button>
      <button class="btn ghost results-feedback" id="feedback">Report a problem</button>
    </div>
    <div id="replayHost"></div>`;
  const again = content.querySelector<HTMLButtonElement>('#again')!;
  again.addEventListener('click', () => {
    rounds?.vote();
    again.disabled = true;
    again.textContent = 'Waiting for the others…';
  });
  content.querySelector('#lobby')!.addEventListener('click', () => backToLobby());
  content.querySelector('#menu')!.addEventListener('click', () => void leaveRoom());
  content.querySelector('#feedback')!.addEventListener('click', (e) =>
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
  const replayBtn = content.querySelector<HTMLButtonElement>('#replay')!;
  replayBtn.addEventListener('click', () => {
    if (race) showReplay(race.state);
  });
  onCleanup(() => {
    resultsMounted = false;
    closeWait.cancel();
  });
  closeWait.poke();
  refreshMultiResults();
}

function refreshMultiResults(): void {
  if (!resultsMounted || !race) return;
  const rc = race;
  const box = content.querySelector<HTMLElement>('#mres');
  if (!box) return;

  if (!winsAwarded && rc.everyoneDone()) {
    winsAwarded = true;
    const w = rc.winner();
    if (w) tally[w.seat] = (tally[w.seat] ?? 0) + 1;
  }

  box.innerHTML = multiResultsHtml(rc.ranked(), rc.selfSeat, rc.mode, rc.par, tally);

  const waiting = content.querySelector<HTMLElement>('#waiting');
  if (waiting) {
    const r = rounds?.state();
    if (r && r.startsInMs !== null) {
      waiting.textContent = `Next yard in ${Math.ceil(r.startsInMs / 1000)}s — ${r.votes.length}/${Math.max(
        r.present.length,
        1,
      )} ready`;
    } else if (!rc.everyoneDone()) {
      const left = [...rc.results.values()].filter((x) => !x.done && !x.left).map((x) => x.name);
      waiting.textContent = left.length ? `Still shunting: ${left.join(', ')}` : '';
    } else if (r && r.votes.length) {
      waiting.textContent = `${r.votes.length}/${Math.max(r.present.length, 1)} ready for another yard`;
    } else {
      waiting.textContent = '';
    }
  }
}

function backToLobby(): void {
  teardown();
  race = null;
  if (net && rounds) showLobby();
  else showMenu();
}

async function leaveRoom(): Promise<void> {
  teardown();
  lobbyMounted = false;
  race = null;
  // Clear the code on the way OUT, or a reload — or reopening from a home-screen icon — drags the
  // player back into a room they deliberately left.
  clearRoomInUrl();
  const n = net;
  const r = rounds;
  net = null;
  rounds = null;
  netSend = () => {};
  try {
    stOff();
  } catch {
    /* channel may already be gone */
  }
  stOff = () => {};
  r?.destroy();
  if (n) {
    try {
      await n.leave();
    } catch {
      /* leaving is best-effort */
    }
  }
  showMenu();
}

// ── boot ────────────────────────────────────────────────────────────────────────
function boot(): void {
  hardenViewport();
  mountShell();
  const params = new URL(location.href).searchParams;
  const room = params.get('room');
  const seedParam = params.get('seed');
  if (room && normalizeRoomCode(room).length >= 3) {
    beginFriends();
  } else if (seedParam) {
    const m = params.get('m');
    if (m) myModeChoice = modeOf(m).id;
    const seedNum = parseInt(seedParam.trim().toLowerCase(), 36);
    if (Number.isFinite(seedNum) && seedNum > 0) {
      clearRoomInUrl();
      startSeed(seedNum >>> 0);
      return;
    }
    showMenu();
  } else {
    showMenu();
  }
  window.addEventListener('beforeunload', () => {
    void net?.leave();
  });
}

// The browser-verification pass drives this to prove the tap → move → solve wiring end-to-end and
// to read state (test-only).
(window as unknown as { __shunt?: unknown }).__shunt = {
  state(): unknown {
    const s = race?.state ?? soloState;
    if (!s) return null;
    return {
      moves: s.moves,
      par: s.par,
      seated: s.seated,
      over: s.over,
      finished: s.finished,
      reason: s.reason,
      undos: s.undos,
      ms: s.elapsedMs,
      locoAt: s.yard.locoAt,
      cars: s.yard.cars.map((c) => c.slice()),
      target: s.yard.target.slice(),
    };
  },
  mode(): string {
    return (race?.state ?? soloState)?.mode.id ?? '';
  },
  live(): boolean {
    return activeLive;
  },
  /** Apply moves through the REAL play path (animation + juice + logic). */
  playSeq(seq: Move[]): unknown {
    for (const m of seq) activePlay?.(m);
    return (window as unknown as { __shunt: { state(): unknown } }).__shunt.state();
  },
  /** Shunt the yard to completion along the proven-optimal line, through the shipped path. */
  solveNow(): unknown {
    const s = race?.state ?? soloState;
    if (!s) return null;
    for (const m of s.puzzle.line) {
      if (s.over) break;
      activePlay?.(m);
    }
    return (window as unknown as { __shunt: { state(): unknown } }).__shunt.state();
  },
  /** Whether a move is currently legal — used to check the tap targets match the rules. */
  legal(m: Move): boolean {
    const s = race?.state ?? soloState;
    return !!s && movesAvailable(s).some((x) => sameMove(x, m));
  },
  carPixel(id: number): { x: number; y: number } | null {
    return activeView?.carPixel(id) ?? null;
  },
  roadPixel(track: number): { x: number; y: number } | null {
    return activeView?.roadPixel(track) ?? null;
  },
  geometry(): unknown {
    return activeView?.geometry() ?? null;
  },
};

boot();
